import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { apiRequest, formatDate, getStatusColor } from '../lib/utils';
import { Loader2 } from 'lucide-react';

const ClaimsPage = () => {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    policy_id: '',
    description: '',
    incident_date: '',
    estimated_amount: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const [claimsData, policiesData] = await Promise.all([
          apiRequest('/claims'),
          apiRequest('/policies')
        ]);
        setClaims(claimsData);
        setPolicies(policiesData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const newClaim = await apiRequest('/claims', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          estimated_amount: parseFloat(formData.estimated_amount) || null
        })
      });
      
      setClaims([newClaim, ...claims]);
      toast.success('Claim submitted successfully');
      setDialogOpen(false);
      setFormData({ policy_id: '', description: '', incident_date: '', estimated_amount: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container-wide">
          <h1>CLAIMS</h1>
          <div className="breadcrumb">
            <Link to="/">Home</Link> &gt; Claims
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="content-section" data-testid="claims-content">
        <div className="container-narrow">
          
          <h2>Filing a Claim</h2>
          <p>
            At Archon Insurance, we understand that filing a claim can be stressful. Our team 
            is here to guide you through the process and advocate on your behalf.
          </p>

          <hr className="content-divider" />

          <h2>How to File a Claim</h2>
          <ul className="content-list">
            <li><strong>Online:</strong> Log into your account and use our secure claims portal.</li>
            <li><strong>Email:</strong> Send claim details to monte@archoninsurance.com</li>
            <li><strong>Phone:</strong> Call us during business hours to speak with a claims specialist.</li>
          </ul>

          <hr className="content-divider" />

          <h2>What You'll Need</h2>
          <ul className="content-list">
            <li>Your policy number</li>
            <li>Date and time of the incident</li>
            <li>Detailed description of what happened</li>
            <li>Photos or documentation of damage (if applicable)</li>
            <li>Police report number (for theft or accidents)</li>
          </ul>

          <hr className="content-divider" />

          {/* Claims Status for logged-in users */}
          {user && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="mb-0">Your Claims</h2>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <button className="btn-navy" disabled={policies.length === 0} data-testid="file-claim-btn">
                      File New Claim
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-[#1a365d]">File a New Claim</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div>
                        <label className="form-label">Select Policy</label>
                        <Select
                          value={formData.policy_id}
                          onValueChange={(value) => setFormData({ ...formData, policy_id: value })}
                        >
                          <SelectTrigger data-testid="claim-policy-select">
                            <SelectValue placeholder="Choose a policy" />
                          </SelectTrigger>
                          <SelectContent>
                            {policies.map((policy) => (
                              <SelectItem key={policy.id} value={policy.id}>
                                {policy.policy_number} - {policy.policy_type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="form-label">Incident Date</label>
                        <Input
                          type="date"
                          value={formData.incident_date}
                          onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                          className="form-input"
                          required
                          data-testid="claim-incident-date"
                        />
                      </div>
                      <div>
                        <label className="form-label">Estimated Damage ($)</label>
                        <Input
                          type="number"
                          value={formData.estimated_amount}
                          onChange={(e) => setFormData({ ...formData, estimated_amount: e.target.value })}
                          placeholder="5000"
                          className="form-input"
                          data-testid="claim-estimated-amount"
                        />
                      </div>
                      <div>
                        <label className="form-label">Description</label>
                        <Textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Describe what happened..."
                          rows={4}
                          required
                          data-testid="claim-description"
                        />
                      </div>
                      <div className="flex gap-3 pt-4">
                        <button type="button" className="btn-outline-navy flex-1" onClick={() => setDialogOpen(false)}>
                          Cancel
                        </button>
                        <button type="submit" className="btn-navy flex-1" disabled={submitting} data-testid="submit-claim-btn">
                          {submitting ? 'Submitting...' : 'Submit Claim'}
                        </button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#1a365d]" />
                </div>
              ) : claims.length === 0 ? (
                <p className="text-gray-500 italic">You haven't filed any claims yet.</p>
              ) : (
                <div className="space-y-4">
                  {claims.map((claim) => (
                    <div key={claim.id} className="border border-gray-200 p-4" data-testid={`claim-card-${claim.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-[#1a365d]">{claim.claim_number}</p>
                          <p className="text-sm text-gray-600 mt-1">{claim.description}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            Incident: {formatDate(claim.incident_date)} | Filed: {formatDate(claim.created_at)}
                          </p>
                        </div>
                        <span className={`badge ${getStatusColor(claim.status)}`}>
                          {claim.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <hr className="content-divider" />
            </>
          )}

          {!user && (
            <>
              <h2>Existing Policyholders</h2>
              <p>Log in to your account to file a claim online and track claim status.</p>
              <div className="mt-4">
                <Link to="/login" className="btn-navy">Client Login</Link>
              </div>
              <hr className="content-divider" />
            </>
          )}

          <h2>Questions?</h2>
          <p>Contact: <a href="mailto:monte@archoninsurance.com">monte@archoninsurance.com</a></p>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClaimsPage;
